import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/useAuthStore'
import { useAccountRestrictions } from '@/shared/accessControl'

import { LoginPage } from '@/features/auth/LoginPage'
import { NotFound } from '@/features/auth/NotFound'

import { AdminLayout } from '@/layouts/AdminLayout'
import { SchoolAdminLayout } from '@/layouts/SchoolAdminLayout'

import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { BookingListPage } from '@/features/bookings/BookingListPage'
import { BookingDetailsPage } from '@/features/bookings/BookingDetailsPage'
import { BookingSessionRequestsPage } from '@/features/bookings/BookingSessionRequestsPage'
import { ChangeTrainerHistoryPage } from '@/features/bookings/ChangeTrainerHistoryPage'
import { AutoReassignIssuesPage } from '@/features/bookings/AutoReassignIssuesPage'
import { AutoReassignHistoryPage } from '@/features/bookings/AutoReassignHistoryPage'
import { TrainerSessionsPage } from '@/features/dashboard/TrainerSessionsPage'
import { UserListPage } from '@/features/users/UserListPage'
import { UserCreatePage } from '@/features/users/UserCreatePage'
import { UserDetailsPage } from '@/features/users/details/UserDetailsPage'
import { SchoolListPage } from '@/features/schools/SchoolListPage'
import { SchoolCreatePage } from '@/features/schools/SchoolCreatePage'
import { SchoolDetailsPage } from '@/features/schools/SchoolDetailsPage'
import { MySchoolPage } from '@/features/schools/MySchoolPage'
import { PackageListPage } from '@/features/packages/PackageListPage'
import { PackageDetailsPage } from '@/features/packages/PackageDetailsPage'
import { PackageCreatePage } from '@/features/packages/PackageCreatePage'
import { AllInOnePackagePage } from '@/features/all-in-one/AllInOnePackagePage'
import { AllInOneEnrollmentsPage } from '@/features/all-in-one/AllInOneEnrollmentsPage'
import { PayoutListPage } from '@/features/payouts/PayoutListPage'
import { PayoutRequestsPage } from '@/features/payouts/PayoutRequestsPage'
import { ReportPage } from '@/features/reports/ReportPage'
import { NotificationPage } from '@/features/notifications/NotificationPage'
import { ReviewPage } from '@/features/reviews/ReviewPage'
import { CertificatePage } from '@/features/certificates/CertificatePage'
import { SystemPage } from '@/features/system/SystemPage'
import { RolesPage } from '@/features/roles/RolesPage'
import { SupportPage } from '@/features/support/SupportPage'
import { SupportInboxPage } from '@/features/support/SupportInboxPage'
import { BannerPage } from '@/features/banners/BannerPage'
import { TodoPage } from '@/features/todo/TodoPage'
import { TrainerProfileUpdatesPage, TrainerProfileLogsPage } from '@/features/todo/TrainerProfileUpdatesPage'
import { TrainerPendingVerificationPage } from '@/features/todo/TrainerPendingVerificationPage'
import { RefundPage } from '@/features/refunds/RefundPage'
import { RefundDetailsPage } from '@/features/refunds/RefundDetailsPage'
import { LoyaltyPage } from '@/features/loyalty/LoyaltyPage'
import { CouponPage } from '@/features/coupons/CouponPage'
import { CollectorPage } from '@/features/collector/CollectorPage'
import { ServicesPage } from '@/features/services/ServicesPage'
import { CategoryPage } from '@/features/category/CategoryPage'
import { CommissionPage } from '@/features/commission/CommissionPage'
import { UnattendedStudentsPage } from '@/features/unattended/UnattendedStudentsPage'
import { HomeListingPage } from '@/features/homelisting/HomeListingPage'
import { UserTrackingPage } from '@/features/tracking/UserTrackingPage'
import { BookingTrackingPage } from '@/features/tracking/BookingTrackingPage'
import { AssistantBookingsPage } from '@/features/assistant-bookings/AssistantBookingsPage'
import { StudentWalletPage } from '@/features/wallet/StudentWalletPage'
import { WithdrawalRequestsPage } from '@/features/wallet/WithdrawalRequestsPage'

function RootRedirect() {
  const { isAuthenticated, user } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <Navigate to={user?.user_type === 'school_admin' ? '/school/overview' : '/dashboard'} replace />
}

/** Booking tracking is super-admin only — a school admin hitting the URL directly
 *  is sent back to their own overview instead of loading the page. */
function SuperAdminOnly({ children }: { children: React.ReactNode }) {
  const user = useAuthStore(s => s.user)
  if (user?.user_type === 'school_admin') return <Navigate to="/school/overview" replace />
  return <>{children}</>
}

/** Hidden from restricted accounts (e.g. sharjah@gov.ae) — direct URLs bounce
 *  back to the dashboard instead of rendering the page. */
function NotRestricted({ children }: { children: React.ReactNode }) {
  const { restricted } = useAccountRestrictions()
  if (restricted) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RootRedirect/>}/>
        <Route path="/login" element={<LoginPage/>}/>

        {/* Admin Routes */}
        <Route path="/dashboard" element={<AdminLayout/>}>
          <Route index element={<DashboardPage/>}/>
          <Route path="booking/list" element={<BookingListPage/>}/>
          <Route path="booking/:id" element={<BookingDetailsPage/>}/>
          <Route path="booking/refund" element={<RefundPage/>}/>
          <Route path="booking/refund/:id" element={<RefundDetailsPage/>}/>
          <Route path="booking/change-trainer-history" element={<ChangeTrainerHistoryPage/>}/>
          <Route path="booking/tracking" element={<SuperAdminOnly><BookingTrackingPage/></SuperAdminOnly>}/>
          <Route path="booking/auto-reassign-issues" element={<AutoReassignIssuesPage/>}/>
          <Route path="booking/auto-reassign-history" element={<AutoReassignHistoryPage/>}/>
          <Route path="user/list" element={<UserListPage/>}/>
          <Route path="user/new" element={<UserCreatePage/>}/>
          <Route path="user/:id/edit" element={<UserCreatePage/>}/>
          <Route path="user/:id" element={<UserDetailsPage/>}/>
          <Route path="school/list" element={<SchoolListPage/>}/>
          <Route path="school/new" element={<SchoolCreatePage/>}/>
          <Route path="school/:id/edit" element={<SchoolCreatePage/>}/>
          <Route path="school/:id" element={<SchoolDetailsPage/>}/>
          <Route path="package/list" element={<PackageListPage/>}/>
          <Route path="package/create" element={<PackageCreatePage/>}/>
          <Route path="package/:id" element={<PackageDetailsPage/>}/>
          <Route path="package/:id/edit" element={<PackageDetailsPage/>}/>
          <Route path="all-in-one" element={<AllInOnePackagePage/>}/>
          <Route path="all-in-one/enrollments" element={<AllInOneEnrollmentsPage/>}/>
          <Route path="payouts/requests" element={<PayoutRequestsPage/>}/>
          <Route path="payouts/trainer" element={<PayoutListPage/>}/>
          <Route path="wallet/students" element={<StudentWalletPage/>}/>
          <Route path="wallet/withdrawals" element={<WithdrawalRequestsPage/>}/>
          <Route path="assistant-bookings" element={<AssistantBookingsPage/>}/>
          {/* Kept for old bookmarks/links to the previous tabbed URLs */}
          <Route path="assistant-bookings/pending" element={<AssistantBookingsPage/>}/>
          <Route path="assistant-bookings/approved" element={<AssistantBookingsPage/>}/>
          <Route path="report/bookings" element={<ReportPage/>}/>
          <Route path="report/revenue" element={<ReportPage/>}/>
          <Route path="report/trainer" element={<ReportPage/>}/>
          <Route path="report/student" element={<ReportPage/>}/>
          <Route path="report/school" element={<ReportPage/>}/>
          <Route path="notifications" element={<NotificationPage/>}/>
          <Route path="review/trainer" element={<ReviewPage/>}/>
          <Route path="review/student" element={<ReviewPage/>}/>
          <Route path="awaiting-certificate" element={<CertificatePage/>}/>
          <Route path="approved-certificate" element={<CertificatePage/>}/>
          <Route path="system/language" element={<SystemPage/>}/>
          <Route path="system/city" element={<SystemPage/>}/>
          <Route path="system/state" element={<SystemPage/>}/>
          <Route path="system/dialect" element={<SystemPage/>}/>
          <Route path="system/pickup" element={<SystemPage/>}/>
          <Route path="system/app-settings" element={<SystemPage/>}/>
          <Route path="system/terms-conditions" element={<SystemPage/>}/>
          <Route path="system/dynamic-services" element={<SystemPage/>}/>
          <Route path="system/auto-reassign" element={<SystemPage/>}/>
          <Route path="roles-and-permission/roles" element={<RolesPage/>}/>
          <Route path="roles-and-permission/permission" element={<RolesPage/>}/>
          <Route path="roles-and-permission/mapping" element={<RolesPage/>}/>
          <Route path="roles-and-permission/users" element={<RolesPage/>}/>
          <Route path="support" element={<SupportInboxPage/>}/>
          {/* Legacy standalone chat path now lives under Support as a tab. */}
          <Route path="chat" element={<Navigate to="/dashboard/support" replace/>}/>
          <Route path="services" element={<ServicesPage/>}/>
          <Route path="dynamic-service/registrations" element={<ServicesPage/>}/>
          <Route path="banner" element={<BannerPage/>}/>
          <Route path="home-slider/list" element={<BannerPage/>}/>
          <Route path="todo/verification" element={<TrainerPendingVerificationPage/>}/>
          <Route path="todo/refund" element={<TodoPage/>}/>
          <Route path="todo/pending-rewards" element={<TodoPage/>}/>
          <Route path="todo/trainer-profile-updates" element={<Navigate to="pending" replace/>}/>
          <Route path="todo/trainer-profile-updates/logs" element={<TrainerProfileLogsPage/>}/>
          <Route path="todo/trainer-profile-updates/:status" element={<TrainerProfileUpdatesPage/>}/>
          <Route path="loyality" element={<LoyaltyPage/>}/>
          <Route path="coupon/list" element={<CouponPage/>}/>
          <Route path="collector/overview" element={<CollectorPage/>}/>
          <Route path="collector/cash-in-hand" element={<CollectorPage/>}/>
          <Route path="collector/collected-list" element={<CollectorPage/>}/>

          {/* Previously-missing features now wired */}
          <Route path="category" element={<NotRestricted><CategoryPage/></NotRestricted>}/>
          <Route path="commission/trainer" element={<CommissionPage/>}/>
          <Route path="commission/certificate-trainer" element={<CommissionPage/>}/>
          <Route path="commission/certificate-drivys" element={<CommissionPage/>}/>
          <Route path="unattended-students" element={<UnattendedStudentsPage/>}/>
          <Route path="home-listing" element={<HomeListingPage/>}/>
          <Route path="tracking" element={<UserTrackingPage/>}/>
          <Route path="loyality/eligible" element={<LoyaltyPage/>}/>
          <Route path="trainer-sessions" element={<TrainerSessionsPage/>}/>
        </Route>

        {/* School Admin Routes */}
        <Route path="/school" element={<SchoolAdminLayout/>}>
          <Route path="overview" element={<DashboardPage/>}/>
          <Route path="booking/list" element={<BookingListPage/>}/>
          <Route path="booking/:id" element={<BookingDetailsPage/>}/>
          <Route path="booking/refund" element={<RefundPage/>}/>
          <Route path="booking/refund/:id" element={<RefundDetailsPage/>}/>
          <Route path="booking/session-requests" element={<BookingSessionRequestsPage/>}/>
          <Route path="booking/auto-reassign-history" element={<AutoReassignHistoryPage/>}/>
          <Route path="trainers-list" element={<UserListPage/>}/>
          <Route path="new-trainer" element={<UserCreatePage/>}/>
          <Route path="trainer/:id/edit" element={<UserCreatePage/>}/>
          <Route path="trainer/:id" element={<UserDetailsPage/>}/>
          <Route path="commission/trainer" element={<CommissionPage/>}/>
          <Route path="commission/certificate-trainer" element={<CommissionPage/>}/>
          <Route path="package-list" element={<PackageListPage/>}/>
          <Route path="package/:id" element={<PackageDetailsPage/>}/>
          <Route path="school-payouts" element={<PayoutListPage/>}/>
          <Route path="notifications" element={<NotificationPage/>}/>
          <Route path="awaiting-certificate" element={<CertificatePage/>}/>
          <Route path="approved-certificate" element={<CertificatePage/>}/>
          <Route path="review/trainer" element={<ReviewPage/>}/>
          <Route path="review/student" element={<ReviewPage/>}/>
          <Route path="report/bookings" element={<ReportPage/>}/>
          <Route path="report/revenue" element={<ReportPage/>}/>
          <Route path="report/trainer" element={<ReportPage/>}/>
          <Route path="report/student" element={<ReportPage/>}/>
          <Route path="report/school" element={<ReportPage/>}/>
          <Route path="roles/list" element={<RolesPage/>}/>
          <Route path="roles/mapping" element={<RolesPage/>}/>
          <Route path="support" element={<SupportPage/>}/>
          <Route path="services" element={<ServicesPage/>}/>
          <Route path="dynamic-service/registrations" element={<ServicesPage/>}/>
          <Route path="banner" element={<BannerPage/>}/>
          <Route path="home-slider/list" element={<BannerPage/>}/>
          <Route path="todo/verification" element={<TrainerPendingVerificationPage/>}/>
          <Route path="todo/refund" element={<TodoPage/>}/>
          <Route path="account" element={<MySchoolPage/>}/>
          <Route path="trainer-sessions" element={<TrainerSessionsPage/>}/>
        </Route>

        <Route path="*" element={<NotFound/>}/>
      </Routes>
    </BrowserRouter>
  )
}
